import React, { useEffect, useState } from 'react';
import { LANGUAGE } from '../config/region';
import { loadLegalDocument } from '../legal';
import { t } from '../i18n';
import './LegalDocument.css';

/* A run of text is either a plain string or [[text, isBold], …] when the source emphasised part of
   it — which the definition lists do for every defined term. */
const Text = ({ value }) => {
  if (typeof value === 'string') return value;
  return (
    <>
      {value.map(([text, bold], i) => (bold ? <strong key={i}>{text}</strong> : <React.Fragment key={i}>{text}</React.Fragment>))}
    </>
  );
};

/* A list item can hold several paragraphs (the author offer has one), separated by a blank line */
const Item = ({ value }) => {
  if (typeof value !== 'string' || !value.includes('\n\n')) return <Text value={value} />;
  const [first, ...rest] = value.split('\n\n');
  return (
    <>
      {first}
      {rest.map((paragraph, i) => <p key={i} className="ld-li-p">{paragraph}</p>)}
    </>
  );
};

const Block = ({ block }) => {
  switch (block.t) {
    case 'h2':
      return (
        <h2 className="ld-h2">
          {block.n && <span className="ld-h2-num">{block.n}</span>}
          <Text value={block.s} />
        </h2>
      );
    case 'clause':
      return (
        <p className="ld-clause">
          <span className="ld-clause-num">{block.n}</span>
          <span className="ld-clause-body"><Text value={block.s} /></span>
        </p>
      );
    case 'ul':
      return (
        <ul className="ld-ul">
          {block.items.map((item, i) => <li key={i}><Item value={item} /></li>)}
        </ul>
      );
    case 'table':
      /* Wide on purpose — the cookie inventory has five columns. It scrolls inside its own box so
         the page body never scrolls sideways on a phone. */
      return (
        <div className="ld-table-wrap">
          <table className="ld-table">
            <thead>
              <tr>{block.head.map((cell, i) => <th key={i}>{cell}</th>)}</tr>
            </thead>
            <tbody>
              {block.rows.map((row, i) => (
                <tr key={i}>{row.map((cell, j) => <td key={j}>{cell}</td>)}</tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    default:
      return <p className="ld-p"><Text value={block.s} /></p>;
  }
};

/*
 * Renders one of the Plotex legal documents in the reader's language.
 *
 * The content is data rather than JSX so it can be regenerated straight from the Word files the
 * documents are revised in — see scripts/generate_legal_docs.py. Nothing here interprets the text;
 * it only decides how a heading, a clause, a bullet and a table look.
 */
const LegalDocument = ({ id }) => {
  const [doc, setDoc] = useState(null);

  useEffect(() => {
    let live = true;
    loadLegalDocument(id, LANGUAGE).then((loaded) => { if (live) setDoc(loaded); });
    return () => { live = false; };
  }, [id]);

  if (!doc) return null;

  return (
    <div className="ld">
      {doc.subtitle && <p className="ld-subtitle">{doc.subtitle}</p>}
      {doc.isTranslation && (
        <p className="ld-fallback">
          {doc.governingLanguage === 'ru'
            ? t('Этот документ публикуется в авторской редакции на русском языке — перевод не имеет юридической силы.')
            : t('Этот документ публикуется в авторской редакции на английском языке — перевод не имеет юридической силы.')}
        </p>
      )}
      {doc.blocks.map((block, i) => <Block key={i} block={block} />)}
    </div>
  );
};

export default LegalDocument;
